import { execSync } from "child_process";
import { writeFileSync } from "fs";
import { createInterface } from "readline";
import { basename, resolve, relative } from "path";
import chalk from "chalk";
import { scanProject } from "../core/scanner.js";
import { loadProject } from "../adapters/dispatch.js";
import { ensureInitialized } from "../detectors/token-estimator.js";
import { analyzeBudget } from "../analyzers/budget.js";
import { analyzeDeadRules } from "../analyzers/dead-rules.js";
import { analyzeStructure } from "../analyzers/structure.js";
import { calculateScore, buildActionPlan } from "../core/scorer.js";
import {
  printCombinedTerminal,
  reportJson,
  reportMarkdown,
} from "../core/reporter.js";
import { removeDeadRules } from "../fixers/remove-dead.js";
import { removeStaleRefs } from "../fixers/remove-stale.js";
import { deduplicateRules } from "../fixers/deduplicate.js";
import {
  buildStructureSuggestions,
  printStructureSuggestions,
  markdownStructureSuggestions,
} from "../fixers/structure-suggestions.js";
import { t, plural, initLocale, getLocale } from "../i18n/index.js";
import { checkSkillUpdate } from "../utils/skill-version.js";
import {
  buildCandidates,
  applyVerdicts,
  loadVerdictsFile,
} from "../verifiers/index.js";
import type { ApplyVerdictsResult } from "../verifiers/index.js";
import { runInstall } from "./install-command.js";
import type { HealthReport } from "../types.js";

// ─── Git helpers ───────────────────────────────────────────────────────────────

function isGitClean(cwd: string): boolean {
  try {
    const out = execSync("git status --porcelain", { cwd, encoding: "utf8" });
    return out.trim().length === 0;
  } catch {
    // Not a git repo or git unavailable — proceed without blocking
    return true;
  }
}

// ─── Types ────────────────────────────────────────────────────────────────────

interface RunCommandOpts {
  format: string;
  tool?: string;
  lang?: string;
  fix?: boolean;
  force?: boolean;
  projectRoot?: string;
  emitCandidates?: string;
  applyVerdicts?: string;
  skipReport?: boolean;
}

interface RunCommandResult {
  exitCode: number;
  errorMessage?: string;
}

// ─── Core logic ───────────────────────────────────────────────────────────────

export async function runAll(
  opts: RunCommandOpts,
  output: { log: typeof console.log; error: typeof console.error } = console,
): Promise<RunCommandResult> {
  initLocale(opts.lang);
  await ensureInitialized();

  const projectRoot = opts.projectRoot ?? process.cwd();
  const scan = scanProject(projectRoot, opts.tool);

  if (scan.tool === "unknown") {
    output.error(t("error.unknownTool"));
    return { exitCode: 1, errorMessage: "unknown tool" };
  }

  if (scan.rootFilePath === null) {
    output.error(t("error.missingRootFile", { tool: scan.tool }));
    return { exitCode: 1, errorMessage: "missing root file" };
  }

  // --fix and --emit-candidates are mutually exclusive
  if (opts.fix && opts.emitCandidates) {
    output.error("--fix and --emit-candidates cannot be used together");
    return {
      exitCode: 1,
      errorMessage: "--fix and --emit-candidates conflict",
    };
  }

  // --fix: require clean working tree unless --force is set
  if (opts.fix && !opts.force && !isGitClean(projectRoot)) {
    output.error(t("error.dirtyWorkingTree"));
    return { exitCode: 1, errorMessage: "dirty working tree" };
  }

  // --emit-candidates: validate output path stays within project root
  if (opts.emitCandidates) {
    const resolved = resolve(opts.emitCandidates);
    const rel = relative(projectRoot, resolved);
    if (rel.startsWith("..")) {
      output.error(
        `--emit-candidates path must be within the project directory: ${opts.emitCandidates}`,
      );
      return {
        exitCode: 1,
        errorMessage: "emit-candidates path outside project",
      };
    }
  }

  const instructions = loadProject(projectRoot, scan.tool);

  const { findings: budgetFindings, summary } = analyzeBudget(instructions);
  const { findings: deadRuleFindings } = analyzeDeadRules(
    instructions,
    projectRoot,
  );
  const { findings: structureFindings } = analyzeStructure(
    instructions,
    projectRoot,
  );

  let allFindings = [
    ...budgetFindings,
    ...deadRuleFindings,
    ...structureFindings,
  ];

  // ── Emit candidates for host LLM verification ────────────────────────────────
  if (opts.emitCandidates) {
    const candidatesFile = buildCandidates(
      allFindings,
      instructions,
      projectRoot,
      getLocale(),
    );
    writeFileSync(opts.emitCandidates, JSON.stringify(candidatesFile, null, 2));
    if (opts.skipReport) return { exitCode: 0 };
  }

  // ── Apply host LLM verdicts ──────────────────────────────────────────────────
  let rejectedByVerification: number | undefined;
  if (opts.applyVerdicts) {
    try {
      const verdictsFile = loadVerdictsFile(opts.applyVerdicts);
      const result: ApplyVerdictsResult = applyVerdicts(
        allFindings,
        verdictsFile,
      );
      allFindings = result.findings;
      rejectedByVerification =
        result.rejectedCount > 0 ? result.rejectedCount : undefined;
    } catch (err) {
      output.error(err instanceof Error ? err.message : String(err));
      return { exitCode: 1, errorMessage: "failed to apply verdicts" };
    }
  }

  const { score, grade } = calculateScore(allFindings, summary);
  const actionPlan = buildActionPlan(allFindings);

  const report: HealthReport = {
    project: basename(projectRoot),
    tool: instructions.tool,
    score,
    grade,
    locale: getLocale(),
    tokenMethod: summary.tokenMethod,
    findings: allFindings,
    budget: summary,
    actionPlan,
    ...(rejectedByVerification !== undefined ? { rejectedByVerification } : {}),
  };

  // ── Apply fixes ──────────────────────────────────────────────────────────────
  if (opts.fix) {
    // Build suggestions BEFORE modifying files, so line numbers are still accurate
    const suggestions = buildStructureSuggestions(allFindings);

    const deadFixed = removeDeadRules(allFindings);
    const staleFixed = removeStaleRefs(allFindings);
    const dupeFixed = deduplicateRules(allFindings);
    const total = deadFixed + staleFixed + dupeFixed;

    if (total === 0) {
      output.log(chalk.green(`  ${t("status.noAutoFixable")}`));
    } else {
      output.log("");
      output.log(chalk.bold.white(`  ${t("label.fixSummary")}`));
      output.log(chalk.gray("  ─".repeat(30)));
      if (deadFixed > 0)
        output.log(
          `  ${chalk.yellow("⚠")}  ${t("fix.removedDeadRules", { count: String(deadFixed), s: plural(deadFixed) })}`,
        );
      if (staleFixed > 0)
        output.log(
          `  ${chalk.yellow("⚠")}  ${t("fix.removedStaleRefs", { count: String(staleFixed), s: plural(staleFixed) })}`,
        );
      if (dupeFixed > 0)
        output.log(
          `  ${chalk.yellow("⚠")}  ${t("fix.removedDuplicates", { count: String(dupeFixed), s: plural(dupeFixed) })}`,
        );
      output.log(chalk.gray("  ─".repeat(30)));
      output.log(
        chalk.green(
          `  ${t("status.fixedIssues", { count: String(total), s: plural(total) })}`,
        ),
      );
      output.log("");
    }

    // Print actionable suggestions for non-auto-fixable structure findings
    printStructureSuggestions(suggestions, projectRoot, output);

    return { exitCode: 0 };
  }

  // ── Skill update notice ──────────────────────────────────────────────────────
  const skillUpdate = checkSkillUpdate(projectRoot);

  // ── Format output ────────────────────────────────────────────────────────────
  if (opts.format === "json") {
    output.log(reportJson(report));
    return { exitCode: 0 };
  }

  if (opts.format === "markdown") {
    const mdSuggestions = buildStructureSuggestions(allFindings);
    const mdExtra = markdownStructureSuggestions(mdSuggestions, projectRoot);
    const updateSection = skillUpdate
      ? [
          "",
          `> ⚠️ **${t("install.outdatedTitle")}** (${t("install.outdatedVersions", { installed: skillUpdate.installedVersion, current: skillUpdate.currentVersion })})`,
          `> \`${t("install.updateCmd", { flag: skillUpdate.isProject ? "--claude-code --project" : "--claude-code" })}\``,
        ]
      : [];
    output.log(reportMarkdown(report, [...mdExtra, ...updateSection]));
    return { exitCode: 0 };
  }

  printCombinedTerminal(report, output);

  if (skillUpdate && process.stdout.isTTY) {
    output.log("");
    output.log(
      `  ${chalk.yellow("⚠")}  ${chalk.bold(t("install.outdatedTitle"))} (${t("install.outdatedVersions", { installed: skillUpdate.installedVersion, current: skillUpdate.currentVersion })})`,
    );
    const confirmed = await promptYesNo(`  ${t("install.updatePrompt")}`);
    if (confirmed) {
      const installResult = runInstall(
        {
          claudeCode: true,
          project: skillUpdate.isProject,
          force: true,
          projectRoot,
        },
        output,
      );
      if (installResult.exitCode !== 0) {
        output.error?.(
          `Update failed: ${installResult.errorMessage ?? "unknown error"}`,
        );
      }
    }
    output.log("");
  }

  return { exitCode: 0 };
}

function promptYesNo(question: string): Promise<boolean> {
  return new Promise((resolve) => {
    const rl = createInterface({
      input: process.stdin,
      output: process.stdout,
    });
    rl.question(`${question} ${chalk.gray("[Y/n]")} `, (answer) => {
      rl.close();
      const trimmed = answer.trim().toLowerCase();
      resolve(trimmed === "" || trimmed === "y");
    });
  });
}
